/**
 * Autosave for the long forms — the owner's choice "C · hybrid" (2026-10-02).
 *
 *  - A form that is not a saved draft yet keeps a DEVICE COPY (utils/formDraftStore)
 *    a moment after every change, and the page restores it after a reload. Nothing
 *    reaches the server until the user presses Save, so an abandoned form leaves no
 *    ghost draft and uses up no reference number.
 *  - A saved DRAFT is written to the server a moment after every change
 *    (`serverSave`); the device copy is kept until the server has it, so a failed
 *    save or a dropped connection still loses nothing.
 *  - Forms with no private draft on the server (the sale wizard, a published
 *    listing) pass no `serverSave` and get the device copy only ("A").
 *
 * Why the device copy at all: iPad Safari reloads on a pull-down and never shows
 * the "leave this page?" dialog (Komala lost a half-keyed OTP to it, 2026-09-17).
 * Here it also covers rotating an iPad across the 1100px phone/desktop switch,
 * which swaps the route tree and unmounts the form: both shells use the same keys.
 *
 * Mirrors cid_admin_portal/src/hooks/useFormAutosave.js. Off during Learn Mode,
 * whose forms hold sandbox data that must never be restored into a real one.
 *
 *   const autosave = useFormAutosave({
 *     draftKey: 'booking-form:new',      // null → no device copy
 *     userId,
 *     readValues: () => values,          // what the device copy keeps
 *     writeValues: (values, meta) => {}, // put a copy back into the form
 *     readMeta: () => ({}),              // optional page state kept alongside
 *     serverSave: async () => {},        // optional — write the server draft
 *     canServerSave: () => true,         // optional — false: wait (say why via waitingText)
 *     serverUpdatedAt,                   // the loaded server copy's updated_at
 *     loadedTick,                        // bump when the form holds its loaded values
 *   });
 *   onValuesChange → autosave.noteChange()
 *   manual save    → const t = autosave.changeToken(); await save(); autosave.markSaved(t);
 */
import { useEffect, useRef, useState } from 'react';
import { saveDeviceDraft, loadDeviceDraft, clearDeviceDraft } from '../lib/formDraftStore';

// Learn Mode (learn/TourContext) marks the body while a lesson runs.
const inLesson = () => document.body.classList.contains('learn-active');

const DEVICE_DELAY_MS = 400;
const SERVER_DELAY_MS = 1500;

export default function useFormAutosave({
  draftKey,
  userId,
  readValues,
  writeValues,
  readMeta,
  serverSave,
  canServerSave,
  serverUpdatedAt,
  loadedTick,
}) {
  const [dirty, setDirty] = useState(false);
  const [deviceSavedAt, setDeviceSavedAt] = useState(null);
  const [restored, setRestored] = useState(null);
  const [server, setServer] = useState({ state: 'idle', at: null, error: null });

  const seq = useRef(0);
  const deviceTimer = useRef(null);
  const serverTimer = useRef(null);
  const inFlight = useRef(false);
  const baseline = useRef(null);
  // The latest callbacks, so a timer set several renders ago calls today's.
  const live = useRef({});
  useEffect(() => {
    live.current = { draftKey, userId, readValues, writeValues, readMeta, serverSave, canServerSave, serverUpdatedAt };
  });

  useEffect(() => () => {
    clearTimeout(deviceTimer.current);
    clearTimeout(serverTimer.current);
  }, []);

  const pushToServer = async () => {
    const L = live.current;
    if (!L.serverSave) return;
    if (L.canServerSave && !L.canServerSave()) {
      setServer({ state: 'waiting', at: null, error: null });
      return;
    }
    if (inFlight.current) {
      serverTimer.current = setTimeout(pushToServer, 800);
      return;
    }
    inFlight.current = true;
    const token = seq.current;
    setServer((cur) => ({ ...cur, state: 'saving', error: null }));
    try {
      await L.serverSave();
      if (seq.current === token) {
        setDirty(false);
        // The server has everything the device copy had; drop it rather than
        // trust two clocks to agree about which is newer on the next load.
        if (L.draftKey) clearDeviceDraft(L.draftKey, L.userId);
      }
      setServer({ state: 'saved', at: new Date().toISOString(), error: null });
    } catch (error) {
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.message || 'Could not save';
      setServer({ state: 'error', at: null, error: msg });
    } finally {
      inFlight.current = false;
    }
  };

  /** Call on every user edit. */
  const noteChange = () => {
    if (inLesson()) return;
    seq.current += 1;
    setDirty(true);
    const L = live.current;
    if (L.draftKey) {
      clearTimeout(deviceTimer.current);
      deviceTimer.current = setTimeout(() => {
        const C = live.current;
        if (!C.draftKey) return;
        const at = saveDeviceDraft(C.draftKey, C.userId, C.readValues(), C.readMeta ? C.readMeta() : {});
        if (at) setDeviceSavedAt(at);
      }, DEVICE_DELAY_MS);
    }
    if (L.serverSave) {
      clearTimeout(serverTimer.current);
      serverTimer.current = setTimeout(pushToServer, SERVER_DELAY_MS);
    }
  };

  /** Before a manual save: which edit it covers. */
  const changeToken = () => seq.current;

  /** After a manual save succeeded: nothing is pending any more (unless typed since). */
  const markSaved = (token = seq.current) => {
    clearTimeout(deviceTimer.current);
    clearTimeout(serverTimer.current);
    const L = live.current;
    if (L.draftKey) clearDeviceDraft(L.draftKey, L.userId);
    setDeviceSavedAt(null);
    setRestored(null);
    if (seq.current === token) setDirty(false);
  };

  /** Drop the device copy (the user chose to discard their changes). */
  const discardDevice = () => {
    clearTimeout(deviceTimer.current);
    const L = live.current;
    if (L.draftKey) clearDeviceDraft(L.draftKey, L.userId);
  };

  // Once the form holds its loaded values: bring back what was typed here and
  // never saved. A server copy newer than the device copy wins — it was saved since.
  useEffect(() => {
    if (!loadedTick || !draftKey || inLesson()) return undefined;
    const timer = setTimeout(() => {
      const L = live.current;
      const draft = loadDeviceDraft(L.draftKey, L.userId);
      if (!draft) return;
      if (L.serverUpdatedAt && new Date(L.serverUpdatedAt) >= new Date(draft.savedAt)) {
        clearDeviceDraft(L.draftKey, L.userId);
        return;
      }
      baseline.current = { values: L.readValues(), meta: L.readMeta ? L.readMeta() : {} };
      L.writeValues(draft.values, draft.meta || {});
      setRestored({ savedAt: draft.savedAt });
      setDeviceSavedAt(draft.savedAt);
      seq.current += 1;
      setDirty(true);
    }, 150);
    return () => clearTimeout(timer);
    // Runs per load, not per render; the live ref carries everything else.
  }, [loadedTick, draftKey]);

  /** "Discard it" on the restored banner: back to what the page had loaded. */
  const discardRestored = () => {
    discardDevice();
    setRestored(null);
    setDeviceSavedAt(null);
    setDirty(false);
    const base = baseline.current;
    if (base) live.current.writeValues(base.values, base.meta || {});
  };

  return {
    dirty,
    deviceSavedAt,
    restored,
    server,
    toServer: !!serverSave,
    noteChange,
    changeToken,
    markSaved,
    discardDevice,
    discardRestored,
    dismissRestored: () => setRestored(null),
  };
}
